import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const useSmallFormatData = (selectedCustomer = null) => {
  // State for API data
  const [jobTypes, setJobTypes] = useState([]);
  const [paperStocks, setPaperStocks] = useState([]);
  const [finishingProcesses, setFinishingProcesses] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [miscCosts, setMiscCosts] = useState([]);
  const [paperSizes, setPaperSizes] = useState({});
  const [finishingMaterials, setFinishingMaterials] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all needed data from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch job types
        const jobTypesResponse = await fetch(`${API_URL}/api/small-format/job-types`, {
          credentials: 'include'
        });
        if (!jobTypesResponse.ok) throw new Error('Failed to fetch job types');
        const jobTypesData = await jobTypesResponse.json();
        setJobTypes(jobTypesData);

        // Fetch paper stocks
        const paperStocksResponse = await fetch(`${API_URL}/api/small-format/paper-stocks`);
        if (!paperStocksResponse.ok) throw new Error('Failed to fetch paper stocks');
        const paperStocksData = await paperStocksResponse.json();
        
        // Process paper stocks to ensure category and display_tier fields exist
        const processedPaperStocks = paperStocksData.map(stock => ({
          ...stock,
          // If the backend doesn't provide these fields, provide defaults
          category: stock.category || categorizeByName(stock.name), // Fallback categorization
          display_tier: stock.display_tier || '' // Default to empty string (not common)
        }));
        
        setPaperStocks(processedPaperStocks);

        // Fetch finishing processes
        const finishingResponse = await fetch(`${API_URL}/api/small-format/finishing-processes`);
        if (!finishingResponse.ok) throw new Error('Failed to fetch finishing processes');
        const finishingData = await finishingResponse.json();
        setFinishingProcesses(finishingData);

        // Fetch printers
        const printersResponse = await fetch(`${API_URL}/api/small-format/printers`);
        if (!printersResponse.ok) throw new Error('Failed to fetch printers');
        const printersData = await printersResponse.json();
        setPrinters(printersData);

        // Fetch miscellaneous costs
        const miscCostsResponse = await fetch(`${API_URL}/api/small-format/misc-costs`);
        if (!miscCostsResponse.ok) throw new Error('Failed to fetch misc costs');
        const miscCostsData = await miscCostsResponse.json();
        setMiscCosts(miscCostsData);

        // Fetch paper sizes
        const paperSizesResponse = await fetch(`${API_URL}/api/small-format/paper-sizes`);
        if (!paperSizesResponse.ok) throw new Error('Failed to fetch paper sizes');
        const paperSizesData = await paperSizesResponse.json();
        setPaperSizes(paperSizesData);
        
        // Fetch Finishing Materials
        const materialsResponse = await fetch(`${API_URL}/api/small-format/finishing-materials`);
        if (!materialsResponse.ok) throw new Error('Failed to fetch finishing materials');
        const materialsData = await materialsResponse.json();
    
        // Group materials by process ID
        const groupedMaterials = materialsData.reduce((acc, material) => {
          const processId = material.finish_process_id;
          if (!acc[processId]) {
            acc[processId] = [];
          }
          acc[processId].push(material);
          return acc;
        }, {});
        setFinishingMaterials(groupedMaterials);

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load pricing data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [API_URL, selectedCustomer?.customer_id]);

  /**
   * Helper function to categorize paper by name when category isn't provided
   * This is a fallback to ensure papers are still categorized even if the backend
   * hasn't been updated with category information
   */
  const categorizeByName = (name) => {
    const nameLower = name.toLowerCase();
    
    if (nameLower.includes('gloss')) return 'Gloss';
    if (nameLower.includes('silk')) return 'Silk';
    if (nameLower.includes('uncoated')) return 'Uncoated';
    if (nameLower.includes('bond')) return 'Bond';
    if (nameLower.includes('recycled')) return 'Recycled';
    if (nameLower.includes('card')) return 'Card';
    
    // Default fallback
    return 'Other';
  };

  return {
    jobTypes,
    paperStocks,
    finishingProcesses,
    printers,
    miscCosts,
    paperSizes,
    finishingMaterials,
    isLoading,
    error,
  };
};